/**
 * Drag-to-resize for the chat drawer (slides + drawer split, and remote iframe + drawer).
 */

export interface AttachDrawerResizeOptions {
  mountPoint: HTMLElement;
  drawer: HTMLElement;
  resizeHandle: HTMLElement;
  getStacked: () => boolean;
  getDesktopFullscreen: () => boolean;
  getMobileFullscreen: () => boolean;
  setDrawerWidthPx: (px: number) => void;
  setStackedHeightPx: (px: number) => void;
  getDrawerWidthBounds: () => { min: number; max: number };
  getStackedHeightBounds: () => { min: number; max: number };
  getCurrentDrawerWidth: () => number;
  getCurrentStackedHeight: () => number;
  onPersistWidth: (px: number) => void;
  onPersistStackedHeight: (px: number) => void;
  onResetWidth: () => void;
  onResetStackedHeight: () => void;
  onResizeTick: () => void;
  /**
   * Remote narrow layout uses column-reverse (drawer above iframe): the split is at the bottom of the drawer.
   * Presentation stacked uses column (slides above drawer): split at the top of the drawer.
   */
  getStackedResizeSeamFromBottom: () => boolean;
}

const DRAG_STEP_KEYBOARD = 12;

export function attachDrawerResize(opts: AttachDrawerResizeOptions): { dispose(): void } {
  const {
    mountPoint,
    drawer,
    resizeHandle,
    getStacked,
    getDesktopFullscreen,
    getMobileFullscreen,
    setDrawerWidthPx,
    setStackedHeightPx,
    getDrawerWidthBounds,
    getStackedHeightBounds,
    getCurrentDrawerWidth,
    getCurrentStackedHeight,
    onPersistWidth,
    onPersistStackedHeight,
    onResetWidth,
    onResetStackedHeight,
    onResizeTick,
    getStackedResizeSeamFromBottom
  } = opts;

  let activePointerId: number | null = null;

  function hideHandle(): boolean {
    return (
      !drawer.classList.contains('rf-open') ||
      getDesktopFullscreen() ||
      getMobileFullscreen()
    );
  }

  function syncHandleOrientation(): void {
    const stacked = getStacked();
    resizeHandle.setAttribute('aria-orientation', stacked ? 'horizontal' : 'vertical');
  }

  function onPointerMove(ev: PointerEvent): void {
    if (activePointerId !== ev.pointerId) return;
    if (hideHandle()) return;

    if (getStacked()) {
      const { min, max } = getStackedHeightBounds();
      const dr = drawer.getBoundingClientRect();
      const seamBottom = getStackedResizeSeamFromBottom();
      const next = seamBottom
        ? Math.round(ev.clientY - dr.top)
        : Math.round(dr.bottom - ev.clientY);
      setStackedHeightPx(Math.min(max, Math.max(min, next)));
    } else {
      const { min, max } = getDrawerWidthBounds();
      const dr = drawer.getBoundingClientRect();
      const next = Math.round(dr.right - ev.clientX);
      setDrawerWidthPx(Math.min(max, Math.max(min, next)));
    }
    onResizeTick();
  }

  function endDrag(ev: PointerEvent): void {
    if (activePointerId !== ev.pointerId) return;
    activePointerId = null;
    mountPoint.classList.remove('rf-drawer-resizing');
    document.body.style.removeProperty('user-select');
    try {
      resizeHandle.releasePointerCapture(ev.pointerId);
    } catch {
      // Ignore if capture already released.
    }
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    window.removeEventListener('pointercancel', endDrag);

    if (getStacked()) {
      onPersistStackedHeight(getCurrentStackedHeight());
    } else {
      onPersistWidth(getCurrentDrawerWidth());
    }
    onResizeTick();
  }

  function onPointerDown(ev: PointerEvent): void {
    if (ev.button !== 0) return;
    if (hideHandle()) return;
    ev.preventDefault();
    activePointerId = ev.pointerId;
    mountPoint.classList.add('rf-drawer-resizing');
    document.body.style.userSelect = 'none';
    try {
      resizeHandle.setPointerCapture(ev.pointerId);
    } catch {
      // Some browsers may not support capture on this element.
    }
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  }

  function onDoubleClick(ev: MouseEvent): void {
    ev.preventDefault();
    if (hideHandle()) return;
    if (getStacked()) {
      onResetStackedHeight();
    } else {
      onResetWidth();
    }
    onResizeTick();
  }

  function onKeyDown(ev: KeyboardEvent): void {
    if (hideHandle()) return;
    if (document.activeElement !== resizeHandle) return;
    let handled = false;
    if (getStacked()) {
      const { min, max } = getStackedHeightBounds();
      const cur = getCurrentStackedHeight();
      const seamBottom = getStackedResizeSeamFromBottom();
      if (seamBottom) {
        if (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') {
          setStackedHeightPx(Math.max(min, cur - DRAG_STEP_KEYBOARD));
          handled = true;
        } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') {
          setStackedHeightPx(Math.min(max, cur + DRAG_STEP_KEYBOARD));
          handled = true;
        }
      } else if (ev.key === 'ArrowUp' || ev.key === 'ArrowLeft') {
        setStackedHeightPx(Math.min(max, cur + DRAG_STEP_KEYBOARD));
        handled = true;
      } else if (ev.key === 'ArrowDown' || ev.key === 'ArrowRight') {
        setStackedHeightPx(Math.max(min, cur - DRAG_STEP_KEYBOARD));
        handled = true;
      }
    } else {
      const { min, max } = getDrawerWidthBounds();
      const cur = getCurrentDrawerWidth();
      if (ev.key === 'ArrowRight' || ev.key === 'ArrowDown') {
        setDrawerWidthPx(Math.min(max, cur + DRAG_STEP_KEYBOARD));
        handled = true;
      } else if (ev.key === 'ArrowLeft' || ev.key === 'ArrowUp') {
        setDrawerWidthPx(Math.max(min, cur - DRAG_STEP_KEYBOARD));
        handled = true;
      }
    }
    if (handled) {
      ev.preventDefault();
      if (getStacked()) {
        onPersistStackedHeight(getCurrentStackedHeight());
      } else {
        onPersistWidth(getCurrentDrawerWidth());
      }
      onResizeTick();
    }
  }

  resizeHandle.addEventListener('pointerdown', onPointerDown);
  resizeHandle.addEventListener('dblclick', onDoubleClick);
  resizeHandle.addEventListener('keydown', onKeyDown);

  const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => syncHandleOrientation()) : null;
  ro?.observe(mountPoint);

  syncHandleOrientation();

  return {
    dispose(): void {
      resizeHandle.removeEventListener('pointerdown', onPointerDown);
      resizeHandle.removeEventListener('dblclick', onDoubleClick);
      resizeHandle.removeEventListener('keydown', onKeyDown);
      ro?.disconnect();
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
    }
  };
}
